import {
  BuildingCatalogEntry,
  BuildingState,
  BuildOrder,
  ResourcePool,
} from "../types/castle";
import { BuildingPaintedIcon } from "./icons";
import { CurrencyIcon, CurrencyKind } from "./CurrencyIcon";
import { BuildingMetaByType } from "../types/buildingMeta";
import { formatDuration, formatNumber, secondsUntil } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";
import { GiHammerNails, GiPadlock } from "react-icons/gi";

interface Props {
  state: BuildingState;
  def: BuildingCatalogEntry | undefined;
  displayName: string;
  playerElement: number | undefined;
  activeOrder: BuildOrder | undefined;
  now: Date;
  castleLevel: number;
  resources: ResourcePool;
  buildSpeedReductionPct: number;
  inventory: Record<string, number>;
  maxAllowedWarHallLevel: number;
  activeBuildOrders: number;
  maxBuildSlots: number;
  onClose: () => void;
  onBuild: () => void;
  onSpeedup: (seconds: number) => void;
  // Inventory-backed speedup: consumes one item of itemId from inventory.
  onApplyInventorySpeedup: (itemId: string) => void;
  buildPending: boolean;
  speedupPending: boolean;
  error: string | null;
  // Optional Academy hook: when this is the Academy and it's at least Lvl 1,
  // we show a "Open research" button that closes the sheet and switches the
  // Castle screen into the research browser.
  onOpenResearch?: () => void;
}

const speedupPresets = [
  { label: "+1м", seconds: 60 },
  { label: "+5м", seconds: 5 * 60 },
  { label: "+30м", seconds: 30 * 60 },
  { label: "+1ч", seconds: 60 * 60 },
];

// Inventory-backed speedup tiers shown in the active-order section. Each
// entry corresponds to an item id from InventoryCatalog; the player can
// use one item per tap. Tiers shown only if player has at least one of
// any tier in inventory (otherwise the row is hidden to reduce clutter).
const inventorySpeedupTiers: Array<{ id: string; label: string; seconds: number }> = [
  { id: "spd-1m",  label: "+1м",  seconds: 60 },
  { id: "spd-5m",  label: "+5м",  seconds: 300 },
  { id: "spd-30m", label: "+30м", seconds: 1800 },
  { id: "spd-1h",  label: "+1ч",  seconds: 3600 },
  { id: "spd-8h",  label: "+8ч",  seconds: 28800 },
  { id: "spd-24h", label: "+24ч", seconds: 86400 },
];

export function BuildingDetailSheet({
  state,
  def,
  displayName,
  playerElement,
  activeOrder,
  now,
  castleLevel,
  resources,
  buildSpeedReductionPct,
  inventory,
  maxAllowedWarHallLevel,
  activeBuildOrders,
  maxBuildSlots,
  onClose,
  onBuild,
  onSpeedup,
  onApplyInventorySpeedup,
  buildPending,
  speedupPending,
  error,
  onOpenResearch,
}: Props) {
  const meta = BuildingMetaByType[state.type];
  const nextLevel = state.level + 1;
  const isUpgrading = !!activeOrder;
  const isMax = def ? state.level >= def.maxLevel : false;
  // Castle-level gate. WarHall has its own ladder (1 / 10 / 20 / paid slot);
  // the server is the source of truth and ships `maxAllowedWarHallLevel`
  // already accounting for the paid flag. For everything else the rule is
  // "next level must not exceed current Castle level".
  const isWarHall = state.type === 11;
  const lockedByCastle = isWarHall
    ? nextLevel > maxAllowedWarHallLevel
    : state.type !== 0 && nextLevel > castleLevel;
  // Human-readable required Castle level for the lock message. For WarHall
  // it mirrors the server ladder; for everything else it equals nextLevel.
  const warHallRequiredCastleLevel = (next: number) => {
    if (next <= 1) return 1;
    if (next === 2) return 10;
    return 20;
  };
  const requiredCastleLevel = isWarHall
    ? warHallRequiredCastleLevel(nextLevel)
    : nextLevel;
  const noFreeBuildSlots =
    !isUpgrading && activeBuildOrders >= maxBuildSlots;

  const levelDef = def?.levels.find((l) => l.level === nextLevel);
  const cost = levelDef?.cost;
  const canAfford = cost
    ? resources.gold >= cost.gold &&
      resources.wood >= cost.wood &&
      resources.stone >= cost.stone &&
      resources.mana >= cost.mana
    : false;
  const remaining = activeOrder ? secondsUntil(activeOrder.finishesAt, now) : 0;

  const effectiveDurationSec = levelDef
    ? Math.ceil(levelDef.durationSeconds * (1 - Math.min(buildSpeedReductionPct, 0.9)))
    : 0;

  const currentProd =
    def?.producedResource != null
      ? def.levels.find((l) => l.level === state.level)?.productionPerHour ?? 0
      : 0;
  const nextProd =
    def?.producedResource != null && levelDef
      ? levelDef.productionPerHour
      : 0;
  const prodDelta = nextProd - currentProd;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-tg-bg p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl bg-tg-secondaryBg">
              <BuildingPaintedIcon
                type={state.type}
                playerElement={playerElement}
                size={36}
              />
            </div>
            <div>
              <div className="text-lg font-semibold">{displayName}</div>
              <div className="text-xs text-tg-hint">
                Lvl {state.level}
                {def ? ` / ${def.maxLevel}` : ""}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-tg-hint"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        {meta && (
          <p className="mb-3 text-sm text-tg-hint">{meta.description}</p>
        )}

        <div className="mb-3 rounded-xl bg-tg-secondaryBg p-3 text-sm">
          <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
            Сейчас
          </div>
          <div className="space-y-0.5">
            <div>Уровень: {state.level}</div>
            {meta?.effectAtLevel(state.level) && (
              <div className="text-tg-hint">{meta.effectAtLevel(state.level)}</div>
            )}
            {def?.producedResource != null && state.level > 0 && (
              <div className="flex items-center gap-1 text-tg-hint">
                <span>Производство: {formatNumber(currentProd)}</span>
                <CurrencyIcon kind={prodResKind(def.producedResource)} size={14} />
                <span>/ч</span>
              </div>
            )}
          </div>
        </div>

        {isUpgrading && activeOrder ? (
          <div className="mb-3 rounded-xl bg-tg-secondaryBg p-3">
            <div className="mb-1 text-xs uppercase tracking-wide text-tg-accent">
              Идёт стройка
            </div>
            <div className="mb-2 text-sm">
              → Lvl {activeOrder.targetLevel} · {formatDuration(remaining)}
            </div>
            <div className="mb-1 text-[10px] uppercase tracking-wide text-tg-hint">
              Применить ускоритель из инвентаря
            </div>
            <div className="grid grid-cols-3 gap-2">
              {inventorySpeedupTiers.map((t) => {
                const count = inventory[t.id] ?? 0;
                const disabled = speedupPending || remaining <= 0 || count <= 0;
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      hapticImpact("light");
                      onApplyInventorySpeedup(t.id);
                    }}
                    className={`flex flex-col items-center gap-0.5 rounded-lg bg-tg-bg px-2 py-2 text-xs disabled:opacity-30 ${
                      count > 0 ? "ring-1 ring-tg-accent/30" : ""
                    }`}
                  >
                    <span className="font-medium">{t.label}</span>
                    <span className="text-[10px] text-tg-hint tabular-nums">
                      x{count}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Free admin-style speedups (kept for owner / Test World) */}
            <div className="mt-2 grid grid-cols-4 gap-1">
              {speedupPresets.map((p) => (
                <button
                  key={p.seconds}
                  type="button"
                  disabled={speedupPending || remaining <= 0}
                  onClick={() => {
                    hapticImpact("light");
                    onSpeedup(p.seconds);
                  }}
                  className="rounded-lg bg-tg-bg/40 px-1 py-1 text-[10px] text-tg-hint disabled:opacity-30"
                >
                  free {p.label}
                </button>
              ))}
            </div>
          </div>
        ) : isMax ? (
          <div className="mb-3 rounded-xl bg-tg-secondaryBg p-3 text-sm text-tg-hint">
            Здание на максимальном уровне.
          </div>
        ) : lockedByCastle ? (
          <div className="mb-3 rounded-xl bg-tg-secondaryBg p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-tg-hint">
              Заблокировано
            </div>
            <div className="mt-1">
              <span className="inline-flex items-center gap-1.5"><GiPadlock size={14} /> Нужен Замок Lvl {requiredCastleLevel} (сейчас {castleLevel}).</span>
            </div>
          </div>
        ) : noFreeBuildSlots ? (
          <div className="mb-3 rounded-xl bg-tg-secondaryBg p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-tg-hint">
              Слоты постройки
            </div>
            <div className="mt-1">
              <span className="inline-flex items-center gap-1.5"><GiHammerNails size={14} /> Все слоты заняты ({activeBuildOrders}/{maxBuildSlots}). Дождись окончания текущей стройки.</span>
            </div>
          </div>
        ) : (
          levelDef && (
            <div className="mb-3 rounded-xl bg-tg-secondaryBg p-3">
              <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
                Следующий уровень
              </div>
              <div className="mb-2 text-sm">→ Lvl {nextLevel}</div>
              {meta?.effectAtLevel(nextLevel) && (
                <div className="text-xs text-tg-hint">
                  {meta.effectAtLevel(nextLevel)}
                </div>
              )}
              {def?.producedResource != null && (
                <div className="flex flex-wrap items-center gap-1 text-xs text-tg-hint">
                  <span>Производство:</span>
                  {state.level > 0 && (
                    <span>{formatNumber(currentProd)} →</span>
                  )}
                  <span>{formatNumber(nextProd)}</span>
                  <CurrencyIcon kind={prodResKind(def.producedResource)} size={14} />
                  <span>/ч</span>
                  {prodDelta > 0 && state.level > 0 && (
                    <span className="ml-1 text-tg-accent">
                      +{formatNumber(prodDelta)}
                    </span>
                  )}
                </div>
              )}
              {cost && (
                <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                  <CostLine kind="gold" have={resources.gold} need={cost.gold} />
                  <CostLine kind="wood" have={resources.wood} need={cost.wood} />
                  <CostLine kind="stone" have={resources.stone} need={cost.stone} />
                  <CostLine kind="mana" have={resources.mana} need={cost.mana} />
                </div>
              )}
              <div className="mt-2 text-xs text-tg-hint">
                ⏱ {formatDuration(effectiveDurationSec)}
                {buildSpeedReductionPct > 0 && (
                  <span className="ml-1 text-tg-accent">
                    (−{Math.round(buildSpeedReductionPct * 100)}%)
                  </span>
                )}
              </div>
            </div>
          )
        )}

        {error && (
          <div className="mb-3 rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
            {error}
          </div>
        )}

        {!isUpgrading && !isMax && !lockedByCastle && !noFreeBuildSlots && cost && (
          <button
            type="button"
            data-tutorial="upgrade-button"
            disabled={!canAfford || buildPending}
            onClick={() => {
              hapticImpact("medium");
              onBuild();
              hapticNotification("success");
            }}
            className="w-full rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText disabled:opacity-40"
          >
            {buildPending ? "Запускаю…" : `Строить → Lvl ${nextLevel}`}
          </button>
        )}

        {/* Academy gateway: when the building is built, surface a second
            button that opens the research grid. Doesn't replace the upgrade
            button — both can sit one above the other. */}
        {onOpenResearch && state.level > 0 && (
          <button
            type="button"
            onClick={() => {
              hapticImpact("light");
              onOpenResearch();
            }}
            className="mt-2 w-full rounded-lg bg-tg-secondaryBg px-4 py-2.5 font-medium text-tg-text"
          >
            Открыть исследования
          </button>
        )}
      </div>
    </div>
  );
}

function CostLine({ kind, have, need }: { kind: CurrencyKind; have: number; need: number }) {
  const ok = have >= need;
  return (
    <div
      className={`flex items-center gap-1 ${
        ok ? "text-tg-text" : "text-tg-destructive"
      }`}
    >
      <CurrencyIcon kind={kind} size={14} />
      {formatNumber(need)}
    </div>
  );
}

function prodResKind(resource: number): CurrencyKind {
  // Gold=0, Wood=1, Stone=2, Mana=3 (matches ResourceType enum in shared).
  switch (resource) {
    case 0: return "gold";
    case 1: return "wood";
    case 2: return "stone";
    case 3: return "mana";
    default: return "gold";
  }
}
